import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, MoreHorizontal, Edit, Trash2, ArrowRightLeft, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportButton from '../components/CSVExportButton';
import AdvancedFilterBuilder from '../components/AdvancedFilterBuilder';

const LEAD_FILTER_FIELDS = [
  { name: 'first_name', label: 'First Name', type: 'text' },
  { name: 'last_name', label: 'Last Name', type: 'text' },
  { name: 'email', label: 'Email', type: 'text' },
  { name: 'phone', label: 'Phone', type: 'text' },
  { name: 'source', label: 'Source', type: 'text' },
  { name: 'status', label: 'Status', type: 'select', options: [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'converted', label: 'Converted' },
    { value: 'lost', label: 'Lost' }
  ]},
  { name: 'created_at', label: 'Created Date', type: 'date' }
];

const LeadsPage = () => {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [advancedFilter, setAdvancedFilter] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [readLeads, setReadLeads] = useState(() => {
    // Load read leads from localStorage
    const stored = localStorage.getItem('readLeads');
    return stored ? JSON.parse(stored) : [];
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: '',
    status: 'new',
    assigned_to: ''
  });

  // Save read leads to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('readLeads', JSON.stringify(readLeads));
  }, [readLeads]);

  // Mark lead as read
  const markAsRead = (leadId) => {
    if (!readLeads.includes(leadId)) {
      setReadLeads(prev => [...prev, leadId]);
    }
  };

  // Check if lead is unread
  const isUnread = (leadId) => !readLeads.includes(leadId);

  useEffect(() => {
    fetchLeads();
    fetchStatuses();
    fetchUsers();
    fetchSavedFilters();
  }, [page, search, statusFilter, advancedFilter]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (advancedFilter) params.append('advanced_filter', JSON.stringify(advancedFilter));
      
      const response = await api().get(`/leads?${params}`);
      setLeads(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await api().get('/master-data?type=lead_status');
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to load statuses');
    }
  };

  const fetchSavedFilters = async () => {
    try {
      const response = await api().get('/saved-filters?entity_type=leads');
      setSavedFilters(response.data);
    } catch (error) {
      console.error('Failed to load saved filters');
    }
  };

  const handleSaveFilter = async (filterData) => {
    try {
      await api().post('/saved-filters', filterData);
      fetchSavedFilters();
    } catch (error) {
      toast.error('Failed to save filter');
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await api().delete(`/saved-filters/${filterId}`);
      fetchSavedFilters();
      toast.success('Filter deleted');
    } catch (error) {
      toast.error('Failed to delete filter');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api().get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api().post('/leads', formData);
      toast.success('Lead created successfully');
      setCreateOpen(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api().put(`/leads/${selectedLead.id}`, formData);
      toast.success('Lead updated successfully');
      setEditOpen(false);
      setSelectedLead(null);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api().delete(`/leads/${id}`);
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleConvert = async (id) => {
    try {
      const response = await api().post(`/leads/${id}/convert`);
      toast.success('Lead converted to application');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const openEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email,
      phone: lead.phone || '',
      source: lead.source || '',
      status: lead.status,
      assigned_to: lead.assigned_to || ''
    });
    setEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      source: '',
      status: 'new',
      assigned_to: ''
    });
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(leads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} lead(s)?`)) return;
    try {
      await Promise.all(selectedLeads.map(id => api().delete(`/leads/${id}`)));
      toast.success(`${selectedLeads.length} lead(s) deleted`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete some leads');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500/10 text-blue-500',
      contacted: 'bg-yellow-500/10 text-yellow-500',
      qualified: 'bg-green-500/10 text-green-500',
      converted: 'bg-amber-500/10 text-amber-500',
      lost: 'bg-red-500/10 text-red-500'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-500/10 text-gray-500';
  };

  const getSourceBadge = (source) => {
    const sourceLower = (source || '').toLowerCase();
    if (sourceLower.includes('facebook') || sourceLower.includes('fb')) {
      return { color: 'bg-blue-600/10 text-blue-600 border-blue-600/20', icon: '📘' };
    }
    if (sourceLower.includes('google') || sourceLower.includes('sheets')) {
      return { color: 'bg-green-600/10 text-green-600 border-green-600/20', icon: '📊' };
    }
    if (sourceLower.includes('web') || sourceLower.includes('form') || sourceLower.includes('website')) {
      return { color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: '🌐' };
    }
    if (sourceLower.includes('instagram') || sourceLower.includes('insta')) {
      return { color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: '📷' };
    }
    if (sourceLower.includes('referral')) {
      return { color: 'bg-amber-600/10 text-amber-600 border-amber-600/20', icon: '👥' };
    }
    if (sourceLower.includes('webhook') || sourceLower.includes('api')) {
      return { color: 'bg-cyan-600/10 text-cyan-600 border-cyan-600/20', icon: '🔗' };
    }
    return { color: 'bg-gray-500/10 text-gray-500 border-gray-500/20', icon: '📝' };
  };

  const LeadForm = ({ onSubmit, buttonText }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>First Name</Label>
          <Input
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            required
            data-testid="lead-firstname-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Last Name</Label>
          <Input
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            required
            data-testid="lead-lastname-input"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Email</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          data-testid="lead-email-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Phone</Label>
        <Input
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          data-testid="lead-phone-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Source</Label>
        <Input
          value={formData.source}
          onChange={(e) => setFormData({ ...formData, source: e.target.value })}
          placeholder="Facebook, Google, Referral..."
          data-testid="lead-source-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value) => setFormData({ ...formData, status: value })}
        >
          <SelectTrigger data-testid="lead-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Assigned To</Label>
        <Select
          value={formData.assigned_to}
          onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
        >
          <SelectTrigger data-testid="lead-assignee-select">
            <SelectValue placeholder="Select user" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" className="w-full" data-testid="lead-submit-btn">
        {buttonText}
      </Button>
    </form>
  );

  return (
    <>
      {/* CSS for unread indicator */}
      <style>{`
        .unread-row {
          border-left: 3px solid #84cc16;
          background: linear-gradient(to right, rgba(132, 204, 22, 0.08), transparent 20%);
        }
        .unread-row:hover {
          background: linear-gradient(to right, rgba(132, 204, 22, 0.12), rgba(0,0,0,0.02) 20%);
        }
        .unread-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #84cc16;
          flex-shrink: 0;
        }
      `}</style>
      
      <div className="space-y-6" data-testid="leads-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Leads</h1>
            <p className="text-muted-foreground">Manage your leads and convert them to applications</p>
          </div>
          <div className="flex items-center gap-2">
            <CSVExportButton entityType="leads" entityLabel="Leads" />
            <CSVImportModal entityType="leads" entityLabel="Leads" onSuccess={fetchLeads} />
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-lead-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Lead
                </Button>
              </DialogTrigger>
              <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Lead</DialogTitle>
                <DialogDescription>Add a new lead to your pipeline</DialogDescription>
              </DialogHeader>
              <LeadForm onSubmit={handleCreate} buttonText="Create Lead" />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search leads..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="lead-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="lead-filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <AdvancedFilterBuilder
              entityType="leads"
              fields={LEAD_FILTER_FIELDS}
              onApply={setAdvancedFilter}
              savedFilters={savedFilters}
              onSaveFilter={handleSaveFilter}
              onDeleteFilter={handleDeleteFilter}
            />
          </div>
          {advancedFilter && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary">Advanced filter active</Badge>
              <Button variant="ghost" size="sm" onClick={() => setAdvancedFilter(null)}>
                Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        {/* Bulk Actions Bar */}
        {selectedLeads.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b">
            <span className="text-sm font-medium">
              {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedLeads([])}>
                Clear Selection
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-leads-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          </div>
        )}
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <p>No leads found</p>
              <Button variant="link" onClick={() => setCreateOpen(true)}>
                Create your first lead
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox 
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-leads-checkbox"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => {
                  const sourceBadge = getSourceBadge(lead.source);
                  const unread = isUnread(lead.id);
                  return (
                  <TableRow 
                    key={lead.id} 
                    data-testid={`lead-row-${lead.id}`}
                    onClick={() => markAsRead(lead.id)}
                    className={`cursor-pointer transition-all duration-200 ${
                      unread 
                        ? 'unread-row' 
                        : 'hover:bg-muted/50'
                    } ${selectedLeads.includes(lead.id) ? 'bg-primary/5' : ''}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                        data-testid={`select-lead-${lead.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {unread && (
                          <span className="unread-dot" />
                        )}
                        {lead.first_name} {lead.last_name}
                      </div>
                    </TableCell>
                    <TableCell>{lead.email}</TableCell>
                    <TableCell>{lead.phone || '-'}</TableCell>
                    <TableCell>
                      {lead.source ? (
                        <Badge variant="outline" className={sourceBadge.color}>
                          <span className="mr-1">{sourceBadge.icon}</span>
                          {lead.source}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(lead)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {lead.status !== 'converted' && (
                            <DropdownMenuItem onClick={() => handleConvert(lead.id)}>
                              <ArrowRightLeft className="w-4 h-4 mr-2" />
                              Convert to Application
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>Update lead information</DialogDescription>
          </DialogHeader>
          <LeadForm onSubmit={handleUpdate} buttonText="Update Lead" />
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default LeadsPage;
